import React from "react";
import PropTypes from "prop-types";
import { Outlet } from "react-router-dom";
import "../theme/tokens.css";
import AppSidebar from "./AppSidebar";
import AppTopbar from "./AppTopbar";

// Layout root shared by every v2 console (ops, pharmacy). The console supplies its own
// `nav`; everything else here is chrome.
//
// Mounting `.ops-v2` activates the v2 theme (see src/v2/theme/tokens.css) for everything
// nested under it. The class name is historical — it predates the second console — and is
// kept because renaming it would touch every `--ops-*` reference in the app for no
// behavioural gain. Read it as "the v2 theme scope", not "the ops console".
//
// `<main>` is the scroll container, not the window: the root is `h-screen
// overflow-hidden` so the sidebar and topbar stay put while content scrolls.
// This is also what makes the reused legacy pages work — they all root at
// `h-full overflow-y-scroll`, which needs an ancestor with a definite height.
// `min-h-0` on `<main>` is load-bearing: a flex child defaults to
// `min-height: auto` and refuses to shrink below its content, which silently
// defeats `overflow-y-auto`.
//
// The root deliberately sets NO text colour. Anything set here is inherited by
// every reused legacy page that doesn't set its own — which is most of a table.
// With `text-[var(--ops-carbon-900)]` every <td> and <th> rendered #101828 on
// /v2/ops/orders but #000000 on /all-orders. The legacy chain (body -> .App ->
// Dashboard) sets no colour at all, so plain black is the correct inherited
// value. The chrome pins its own colour instead — see AppSidebar / AppTopbar.
function AppShell({ nav, brandLabel, actions }) {
    return (
        <div className="ops-v2 flex h-screen w-full overflow-hidden bg-[var(--ops-carbon-50)]">
            <AppSidebar nav={nav} brandLabel={brandLabel} />
            <div className="flex min-w-0 flex-1 flex-col">
                <AppTopbar actions={actions} />
                <main className="min-h-0 min-w-0 flex-1 overflow-y-auto">
                    <Outlet />
                </main>
            </div>
        </div>
    );
}

AppShell.propTypes = {
    nav: PropTypes.array.isRequired,
    brandLabel: PropTypes.string,
    actions: PropTypes.array,
};

AppShell.defaultProps = {
    brandLabel: undefined,
    actions: undefined,
};

export default AppShell;
